import Autocomplete from '@mui/material/Autocomplete';
import { SxProps, Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import { DocumentType } from 'common/models/Document';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GetDocumentTypesByEntityId } from 'services/DocumentsService';

interface Props {
  value: number;
  entityTypeId: number;
  label?: string;
  appendToStart?: DocumentType[];
  sx?: SxProps<Theme>;
  changeHandler: (type: DocumentType | null) => void;
}

export default function DocumentTypePicker({
  value,
  entityTypeId,
  label = 'Document Type',
  appendToStart,
  sx,
  changeHandler,
}: Props) {
  const [options, setOptions] = useState<DocumentType[]>([]);

  useEffect(() => {
    const getTypes = async () => {
      const res = await GetDocumentTypesByEntityId(entityTypeId);
      if (res) {
        if (appendToStart && appendToStart.length > 0) setOptions([...appendToStart, ...res]);
        else setOptions(res);
      }
    };
    getTypes();
  }, [entityTypeId, appendToStart]);

  const selectedOption = useMemo(() => {
    const type = options.find((o) => o.id === value);
    if (type) return type;
    return null;
  }, [value, options]);

  const localChangeHandler = useCallback(
    (e: React.SyntheticEvent, v: DocumentType | null) => {
      changeHandler(v);
    },
    [changeHandler]
  );

  return (
    <Autocomplete
      value={selectedOption}
      options={options}
      getOptionLabel={(o) => o.name}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      onChange={localChangeHandler}
      sx={sx}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          slotProps={{ htmlInput: { ...params.inputProps, 'data-lpignore': 'true' } }}
        />
      )}
    />
  );
}
